import type { AxiosInstance } from 'axios'
import { computed, ref } from 'vue'

import apiClient from '@/api/client'

interface FeatureFlags {
  partner_dashboard_enabled: boolean
}

const _flags = ref<FeatureFlags | null>(null)
let _inflight: Promise<void> | null = null

/** Test-only: reset module-scoped state between tests. */
export function _resetFeatureFlagsForTests(): void {
  _flags.value = null
  _inflight = null
}

export function useFeatureFlags(client: AxiosInstance = apiClient) {
  async function _load(): Promise<void> {
    try {
      const r = await client.get('/health/feature-flags')
      _flags.value = r.data
    } catch (err) {
      // Fail-open: a transient blip (network, cold-start, gateway timeout) must
      // NOT lock partners on the Coming-Soon view until they reload the SPA.
      // Operators can still explicitly disable the dashboard via the server-side
      // flag — when the endpoint succeeds with `false` we honour it.
      console.warn('[useFeatureFlags] flag fetch failed; assuming enabled', err)
      _flags.value = { partner_dashboard_enabled: true }
    } finally {
      // Clear the in-flight guard so the NEXT ensureLoaded() can retry on
      // failure rather than reusing a resolved promise that masks the error.
      _inflight = null
    }
  }

  async function ensureLoaded(): Promise<void> {
    if (_flags.value) return
    if (!_inflight) _inflight = _load()
    await _inflight
  }

  const partnerDashboardEnabled = computed(() => _flags.value?.partner_dashboard_enabled ?? false)

  return { ensureLoaded, partnerDashboardEnabled }
}
